import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Loader2, UserCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth, type Profile } from "@/contexts/AuthContext";
import { ROLE_LABELS, ALL_ROLES, type AppRole } from "@/contexts/roles";
import { getRedirectForProfile } from "./LoginPage";

// Les 6 rôles utilisateur réels (cf. ALL_ROLES), qui sont exactement les
// valeurs de l'enum Postgres `user_role`. Les variantes splitées
// (`collaborateur_els` / `collaborateur_afr`) sont des buckets de permissions,
// pas des rôles de login. Les deux buckets publication ont été supprimés le
// 2026-08-10 (migrations 113/114).
const PICKER_ROLES = ALL_ROLES;

// Mapping slug → label des métiers (table `metiers` côté Supabase).
// Hardcodé ici car la table metiers est restreinte aux rôles authentifiés
// (RLS) et SelectUserPage tourne en pré-login (anon). À synchroniser
// manuellement si on ajoute un métier en DB.
const METIER_LABELS: Record<string, string> = {
  "expert-peb": "Expert PEB",
  "etat-des-lieux": "État des lieux",
  "certificat-elec": "Certificat Élec",
  "nettoyage": "Nettoyage",
  "photos-videos": "Photos & Vidéos",
};
const formatSpecialite = (slug: string) => METIER_LABELS[slug] ?? slug;

export function SelectUserPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, enterDevSession, profile } = useAuth();
  const [searchParams] = useSearchParams();
  const [selectedRole, setSelectedRole] = useState<AppRole | null>(null);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedRole) {
      setProfiles([]);
      return;
    }
    let cancelled = false;
    setLoadingProfiles(true);
    setLoadError(null);
    // Passe par le RPC SECURITY DEFINER (migration 031) car la RLS bloque le
    // SELECT direct quand auth.uid() est NULL (cas du picker pré-login).
    supabase
      .rpc("get_profiles_for_role", { p_role: selectedRole })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setLoadError(error.message);
          setProfiles([]);
        } else {
          const rows = (data ?? []) as Profile[];
          const filtered = rows
            .filter((p) => p.role === selectedRole)
            .sort((a, b) => (a.first_name ?? "").localeCompare(b.first_name ?? ""));
          setProfiles(filtered);
        }
        setLoadingProfiles(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRole]);

  // Garde-fou : ?dev requis pour acceder au picker role. L externe qui
  // taperait /login/select-user en dur est redirige vers /login.
  // (Placé APRÈS les hooks pour respecter les rules-of-hooks.)
  if (!searchParams.has("dev")) {
    return <Navigate to="/login" replace />;
  }

  if (isAuthenticated) {
    return <Navigate to={getRedirectForProfile(profile)} replace />;
  }

  const handlePickProfile = (p: Profile) => {
    enterDevSession(p);
    navigate(getRedirectForProfile(p), { replace: true });
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <h1 className="text-sm font-medium text-text-primary">
          {selectedRole ? t("auth.selectUser") : t("auth.selectRole")}
        </h1>
        {selectedRole && (
          <button
            type="button"
            onClick={() => setSelectedRole(null)}
            className="flex items-center gap-1 text-xs text-text-muted hover:text-accent transition-colors cursor-pointer"
          >
            <ChevronLeft size={14} />
            {t("common.back")}
          </button>
        )}
      </header>

      {!selectedRole && (
        <div className="grid grid-cols-2 gap-2">
          {PICKER_ROLES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setSelectedRole(r)}
              className="bg-surface border border-border hover:border-accent hover:bg-accent/5 text-text-primary text-xs font-medium px-3 py-3 rounded-md transition-colors cursor-pointer text-left"
            >
              {ROLE_LABELS[r]}
            </button>
          ))}
        </div>
      )}

      {selectedRole && (
        <div className="flex flex-col gap-2">
          <p className="text-[10px] text-text-muted uppercase tracking-wider">
            {ROLE_LABELS[selectedRole]}
          </p>

          {loadingProfiles && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-accent" />
            </div>
          )}

          {!loadingProfiles && loadError && (
            <p className="text-sm text-red-500">{loadError}</p>
          )}

          {!loadingProfiles && !loadError && profiles.length === 0 && (
            <p className="text-xs text-text-muted py-4 text-center">
              {t("common.noResults")}
            </p>
          )}

          {!loadingProfiles && !loadError && profiles.length > 0 && (
            <ul className="flex flex-col gap-1">
              {profiles.map((p) => {
                // Pour les prestataires, on affiche la spécialité à côté
                // du nom (aide visuelle pour distinguer ELS / PEB / etc.).
                const specs = ((p as unknown as { specialites?: string[] }).specialites) ?? [];
                const specLabel = selectedRole === "prestataire" && specs.length > 0
                  ? formatSpecialite(specs[0])
                  : null;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => handlePickProfile(p)}
                      className="w-full flex items-center gap-2 bg-surface border border-border hover:border-accent hover:bg-accent/5 px-3 py-2 rounded-md transition-colors cursor-pointer text-left"
                    >
                      <UserCircle size={16} className="text-accent flex-shrink-0" />
                      <span className="flex-1 text-sm text-text-primary">
                        {p.first_name || p.last_name
                          ? `${p.first_name} ${p.last_name}`.trim()
                          : p.email}
                      </span>
                      {specLabel && (
                        <span className="text-[10px] text-text-muted uppercase tracking-wider flex-shrink-0">
                          {specLabel}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <div className="text-center pt-2 border-t border-border">
        <Link
          to="/login?dev"
          className="text-xs text-text-muted hover:text-accent"
        >
          {t("auth.backToLogin")}
        </Link>
      </div>
    </div>
  );
}
